import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { Bot, BotListResponse } from '../../lib/types.js';

/** Mirrors the API's caps (sc-platform core/platform/bots/schemas.ts). */
export const MAX_BOT_NAME_CHARS = 40;
export const MAX_BOT_DESCRIPTION_CHARS = 140;
export const MAX_BOT_INSTRUCTIONS_CHARS = 2_000;

export interface BotDraft {
  name: string;
  description: string;
  instructions: string;
  enabled: boolean;
  groupIds: string[];
}

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

/**
 * One save for the whole form. The API takes groups on their own endpoint and creates
 * bots switched off, so a new bot is created, then switched on and given its groups.
 */
const saveBot = async ({ id, draft }: { id: string | null; draft: BotDraft }): Promise<Bot> => {
  const { groupIds, ...fields } = draft;
  let bot = id
    ? await apiRequest<Bot>(`/api/bots/${id}`, { method: 'PATCH', body: fields })
    : await apiRequest<Bot>('/api/bots', {
        method: 'POST',
        body: {
          name: fields.name,
          description: fields.description,
          instructions: fields.instructions,
        },
      });
  if (!id && fields.enabled) {
    bot = await apiRequest<Bot>(`/api/bots/${bot.id}`, {
      method: 'PATCH',
      body: { enabled: true },
    });
  }
  if (!sameSet(bot.groupIds, groupIds)) {
    bot = await apiRequest<Bot>(`/api/bots/${bot.id}/groups`, {
      method: 'PUT',
      body: { groupIds },
    });
  }
  return bot;
};

export const useBotsLogic = (tenantId: string | undefined) => {
  const queryClient = useQueryClient();
  const key = queryKeys.bots(tenantId ?? '');

  const query = useQuery<BotListResponse>({
    queryKey: key,
    queryFn: () => apiRequest<BotListResponse>('/api/bots'),
    enabled: Boolean(tenantId),
  });

  const save = useMutation<Bot, Error, { id: string | null; draft: BotDraft }>({
    mutationFn: saveBot,
    // Refresh even after a partial failure: a create can succeed before its groups do.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  return {
    isLoading: query.isLoading,
    error: query.error,
    bots: query.data?.bots ?? [],
    /** Create when `id` is null, else update. */
    save: (id: string | null, draft: BotDraft) => save.mutateAsync({ id, draft }),
    isSaving: save.isPending,
    saveError: save.error,
    resetSave: () => save.reset(),
  };
};
